import type { HelpRole, Permission } from '@hoopoe/shared';
import { type QueryClient, useQuery } from '@tanstack/react-query';

export interface Me {
  id: string;
  email: string;
  name: string | null;
  role: HelpRole;
  totpEnrolled: boolean;
  /** From the server's permission matrix (PLAN §11), so the UI never disagrees with the API. */
  permissions: Permission[];
}

export type SessionState =
  | { status: 'signed-in'; user: Me }
  | { status: 'needs-totp'; next: 'enrol' | 'totp'; email: string }
  | { status: 'signed-out' };

export const sessionQuery = {
  queryKey: ['me'],
  queryFn: async (): Promise<SessionState> => {
    const r = await fetch('/api/me');
    if (r.ok) return { status: 'signed-in', user: (await r.json()) as Me };
    const body = (await r.json().catch(() => ({}))) as { error?: string; next?: 'enrol' | 'totp'; email?: string };
    if (body.error === 'totp_required' && body.next) return { status: 'needs-totp', next: body.next, email: body.email ?? '' };
    return { status: 'signed-out' };
  },
  staleTime: 60_000,
};

export const loadSession = (qc: QueryClient) => qc.ensureQueryData(sessionQuery);

export function useMe() {
  const q = useQuery(sessionQuery);
  return { ...q, data: q.data?.status === 'signed-in' ? q.data.user : null };
}

export const useRole = (): HelpRole => useMe().data?.role ?? 'viewer';

/** Mirrors the API's permission check; hide or disable what the user can't do. */
export const useCan = (permission: Permission): boolean => useMe().data?.permissions.includes(permission) ?? false;

/** Screens reachable without signing in. */
export const PUBLIC_PATHS = ['/login', '/request-access', '/set-password'];
export const isPublicPath = (p: string) => PUBLIC_PATHS.some(x => p === x || p.startsWith(`${x}/`));
